if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      handleResize = () => {
        this.setTopMargin();
        this.positioningProductInfo();
        this.syncStickyHeaderThreshold();
      };

      _updateProgressBar = null;

      reinitAfterDelay = () => setTimeout(() => this.reinit(), 0);

      constructor() {
        super();
        /* Wrapper is outside the section; closest("section") is always null */
        this.section = this.querySelector("section.wt-product__wrapper");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
        this.style.marginTop = "";
        const outer = this.closest(".shopify-section");
        if (outer) {
          outer.style.removeProperty("--am-pdp-header-overlap");
        }
        const section =
          this.querySelector("section.wt-product__wrapper") || this.section;
        if (section) section.style.marginTop = "";
        if (this._productInfoRo) {
          this._productInfoRo.disconnect();
          this._productInfoRo = null;
        }
        window.removeEventListener("resize", this.handleResize);
        if (this._updateProgressBar) {
          window.removeEventListener("scroll", this._updateProgressBar);
          this._updateProgressBar = null;
        }
        if (this._headerObserver) {
          this._headerObserver.disconnect();
          this._headerObserver = null;
        }
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      isFirstSection() {
        const root = document.querySelector("#root");
        const outer = this.closest(".shopify-section");
        if (!root || !outer) return false;
        /* First Shopify section in main (ignores non-section nodes between sections). */
        const sections = [...root.children].filter((el) =>
          el.classList?.contains("shopify-section"),
        );
        return sections[0] === outer;
      }

      /**
       * Sticky header sentinel height must track masonry gallery height. It was only set on
       * init/reinit; after a viewport shrink the old inline height (e.g. ~10k px) inflated
       * document scrollHeight and caused a huge gap below the footer (see debug logs).
       */
      syncStickyHeaderThreshold() {
        const stickyHeaderThreshold = document.querySelector(
          ".sticky-header__threshold",
        );
        if (!stickyHeaderThreshold) return;
        if (!this.isTransparentHeaderEnabled()) {
          stickyHeaderThreshold.style.removeProperty("height");
          return;
        }
        if (!document.body.classList.contains("page-header-sticky")) {
          stickyHeaderThreshold.style.removeProperty("height");
          return;
        }
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (masonry && masonry.offsetHeight > 0) {
          stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
        } else {
          stickyHeaderThreshold.style.removeProperty("height");
        }
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        if (this._headerObserver) this._headerObserver.disconnect();
        this._headerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        this._headerObserver.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        /* Prefer #header (page-header); avoid outer <header> section wrapper height (can include sibling drawer markup). */
        const headerBar =
          document.getElementById("header") ||
          document.querySelector(".wt-header");
        const pullUp =
          this.isTransparentHeaderEnabled() && this.isFirstSection();
        if (!pullUp) return 0;

        let headerHeight = headerBar?.offsetHeight ?? 0;
        /*
         * Amateur header (option_6): .wt-header__body uses padding (e.g. 16px top). The host
         * #header offsetHeight can still leave a visible band above the gallery; add body
         * padding-top so media meets the viewport edge under the overlay bar.
         */
        if (headerBar?.classList?.contains("wt-header--amateur-custom")) {
          const bodyEl = headerBar.querySelector(".wt-header__body");
          if (bodyEl) {
            const pt = parseFloat(getComputedStyle(bodyEl).paddingTop);
            if (!Number.isNaN(pt) && pt > 0) {
              headerHeight += pt;
            }
          }
        }

        return headerHeight;
      }

      setTopMargin() {
        const section =
          this.querySelector("section.wt-product__wrapper") || this.section;
        const outer = this.closest(".shopify-section");
        if (!section) return;
        /* Header overlap pull-up matches sliderEnabledBreakpoint (module-product-gallery.css @ 900px). */
        const desktopOverlap = window.matchMedia("(min-width: 900px)").matches;
        if (!desktopOverlap) {
          if (outer) outer.style.removeProperty("--am-pdp-header-overlap");
          this.style.marginTop = "";
          section.style.marginTop = "";
          return;
        }

        /*
         * Clear any previously-set value first so [data-gallery] is at its true
         * natural layout position when we measure it. Using the header's offsetHeight
         * as a proxy was unreliable: on this layout the header padding is already
         * included in offsetHeight, and it was being double-counted. Measuring the
         * gallery's actual distance from the viewport top is always correct — if the
         * gallery is already at Y=0 (as it is here with position:absolute header and
         * no section padding), the offset naturally computes to 0 and no transform
         * is applied.
         *
         * One custom property on the outer section (see module-product-gallery.css):
         * only `[data-gallery]` / `#gallery-loader` use it (translateY).
         */
        if (outer) outer.style.removeProperty("--am-pdp-header-overlap");

        const pullUp =
          this.isTransparentHeaderEnabled() && this.isFirstSection();
        if (!pullUp) {
          this.style.marginTop = "";
          section.style.marginTop = "";
          return;
        }

        const gallery = this.querySelector("[data-gallery]");
        const naturalTop = gallery
          ? Math.max(0, Math.round(gallery.getBoundingClientRect().top))
          : 0;

        if (outer && naturalTop > 0) {
          outer.style.setProperty(
            "--am-pdp-header-overlap",
            `${naturalTop}px`,
          );
        }
        this.style.marginTop = "";
        section.style.marginTop = "";
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return !!(
          header?.dataset?.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        this._updateProgressBar = () => {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        };

        window.addEventListener("scroll", this._updateProgressBar);

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            if (fullImage) fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        if (!productInfo) return;

        const mq = window.matchMedia("(min-width: 900px)");
        const applyTop = () => {
          if (!mq.matches) {
            productInfo.style.top = "";
            return;
          }
          const header = document.querySelector(".wt-header");
          const headerH = header ? header.offsetHeight : 0;
          const gap = 16;
          const minTop = headerH + gap;
          const centered = (window.innerHeight - productInfo.offsetHeight) / 2;
          productInfo.style.top = `${Math.max(centered, minTop)}px`;
        };

        if (this._productInfoRo) {
          this._productInfoRo.disconnect();
        }
        this._productInfoRo = new ResizeObserver(applyTop);
        this._productInfoRo.observe(productInfo);
        applyTop();
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize);
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        if (this.isTransparentHeaderEnabled()) {
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              this.syncStickyHeaderThreshold();
            });
          }
          this.setTopMargin();
          requestAnimationFrame(() =>
            requestAnimationFrame(() => this.setTopMargin()),
          );
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.syncStickyHeaderThreshold();
          }
          this.setTopMargin();
          requestAnimationFrame(() =>
            requestAnimationFrame(() => this.setTopMargin()),
          );
          this.observeHeader();
          this.attachEvents();
        }
        this.positioningProductInfo();
      }
    },
  );
}
